import type { ReactNode } from "react";
import { Link } from "react-router-dom";

export type PageHeaderProps = {
  title: string;
  /** When true, use bare h2 (historical activities page). */
  plainTitle?: boolean;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
};

/**
 * Historical `ne__page-header` block used across list/detail screens.
 * Live Angular always uses `ne__page-header__title` on the h2 (33px / weight 300).
 */
export function PageHeader({
  title,
  plainTitle = false,
  actions,
  children,
  className = "",
}: PageHeaderProps) {
  return (
    <header className={`ne__page-header${className ? ` ${className}` : ""}`}>
      <div className="ne__page-header__header">
        <h2
          className={
            plainTitle
              ? "ne__page-header__title"
              : "ne__page-header__title"
          }
        >
          {title}
        </h2>
      </div>
      {actions ? (
        <ul className="ne__page-header__nav-secondary">
          <li>{actions}</li>
        </ul>
      ) : null}
      {children}
    </header>
  );
}

export type PageHeaderActionProps = {
  title?: string;
  onClick?: () => void;
  /** When set, render as a link (compose / invite / create). */
  to?: string;
  icon?: string;
  label?: string;
  variant?: "icon" | "text";
};

/** Halo secondary action (invite / compose / add). */
export function PageHeaderAction({
  title,
  onClick,
  to,
  icon = "ne__icon__add",
  label,
  variant = "icon",
}: PageHeaderActionProps) {
  const className =
    variant === "text"
      ? "ne__btn ne__btn--secondary ne__btn--secondary--halo ne__btn--md"
      : "ne__btn ne__btn--secondary ne__btn--secondary--halo ne__btn--icon ne__btn--lg";
  const content = (
    <>
      {label ? <span>{label}</span> : null}
      {icon ? (
        <i className={variant === "icon" ? `ne__icon ${icon}` : icon} />
      ) : null}
    </>
  );
  if (to) {
    return (
      <Link className={className} to={to} title={title}>
        {content}
      </Link>
    );
  }
  return (
    <button type="button" className={className} title={title} onClick={onClick}>
      {content}
    </button>
  );
}
